import { BaseWebComponentView } from '../../../../../framework/BaseWebComponentView';
import { MspInProductTrialsOverlayWindowType } from '../types/MspInProductTrialsOverlayWindowType';
import { GzInfoOverlayWebComponent, GzOverlay } from '../../../../../ui/gzInfoOverlay';
import {
  getMspInProductTrialsOverlayWindowConfigByWindowType,
  getTargetForMenuStateByMenuItem,
  MenuItem,
  MenuState,
  MspInProductTrialsAdditionalUserSettings,
  MspInProductTrialsOverlayState,
  MspInProductTrialsOverlayWindowDetails,
} from './configs/msp-in-product-trials-overlay-window-config';
import { MspInProductTrialsOverlayContentWebComponent } from '../../mspInProductTrialsOverlayContent';
import {
  MspInProductTrialsOverlayWindowEventType,
  OnMspInProductTrialsOverlayWindowButtonClick,
} from '../../mspInProductTrialsOverlayContent/events/MspInProductTrialsOverlayWindowEventType';
import { toggleVisibility } from '../../../../../utils/htmlElement.utils';
import { CompaniesAppWebComponent } from '../../../../../companies/companiesApp';
import { CompaniesGridWebComponent } from '../../../../../companies/companiesGrid';
import { GridViewWebComponent } from '../../../../../grid/gridView';
import { MspInProductTrialStatus } from '../../../../../company/types/msp-in-product-trial-status';
import {
  WEB_COMPONENT_COMPANIES_APP,
  WEB_COMPONENT_EDIT_COMPANY_PANEL,
  WEB_COMPONENT_MSP_IN_PRODUCT_TRIALS_OVERLAY_CONTENT,
} from '../../../../../webcomponents.constants';
import { ColumnKeys, FILTERS } from '../../../../../companies/companiesGrid/view/gridConfig/companies-grid-columns';
import { OverlayPosition } from '../../../../../ui/gzInfoOverlay/type';
import { OnCompanyNameClick } from '../../../../../companies/companiesGrid/events/events';
import { EditCompanyPanelWebComponent } from '../../../../../company/editCompany/editCompanyPanel';
import { TabIndex } from '../../../../../company/editCompany/editCompanyPanel/view/EditCompanyPanelTypes';
import { NavigationHelper } from '@nirvana/shared/navigation';

export class MspInProductTrialsOverlayWindowsAppView extends BaseWebComponentView {
  public mspInProductTrialsOverlayState: number | undefined;
  public overlayWindow: MspInProductTrialsOverlayWindowDetails;

  private menuState: MenuState;
  private MSP_IN_PRODUCT_TRIALS_OVERLAYS_TRACKING_NAME = 'msp-in-product-trials';

  buildSuccessView(additionalUserSettings: MspInProductTrialsAdditionalUserSettings) {
    this.mspInProductTrialsOverlayState = additionalUserSettings?.mspInProductTrialsOverlayState;
  }

  public setMenuState(menuState: MenuState): void {
    if (this.menuState === menuState) {
      return;
    }

    this.menuState = menuState;

    if (this.overlayWindow && this.overlayWindow.type === MspInProductTrialsOverlayWindowType.menuOverlay) {
      this.updateMenuOverlayPosition(false);
    }
  }

  public onMenuScrollEvent() {
    if (this.overlayWindow && this.overlayWindow.type === MspInProductTrialsOverlayWindowType.menuOverlay) {
      this.updateMenuOverlayPosition(true);
    }
  }

  private onGridScrollEvent(gridElement: GridViewWebComponent<any, any>, targetElement: HTMLElement) {
    if (this.overlayWindow) {
      this.updateCompaniesOverlayPosition(gridElement, targetElement);
    }
  }

  public async createMenuOverlay(): Promise<void> {
    if (
      this.mspInProductTrialsOverlayState !== undefined &&
      [MspInProductTrialsOverlayState.SoftDismissed, MspInProductTrialsOverlayState.CompletelyDismissed].includes(
        this.mspInProductTrialsOverlayState
      )
    ) {
      return;
    }

    this.overlayWindow = { type: MspInProductTrialsOverlayWindowType.menuOverlay };

    const config = getMspInProductTrialsOverlayWindowConfigByWindowType(this.overlayWindow.type, this.menuState);
    const targetsByMenuItem = getTargetForMenuStateByMenuItem(this.menuState);
    const targetElement = Ext.getCmp(targetsByMenuItem[MenuItem.Company]).getEl().dom;

    if (!targetElement) {
      return;
    }

    const mspInProductTrialsOverlayContentWebComponent = document.createElement(
      WEB_COMPONENT_MSP_IN_PRODUCT_TRIALS_OVERLAY_CONTENT
    ) as MspInProductTrialsOverlayContentWebComponent;

    mspInProductTrialsOverlayContentWebComponent.setAttribute(
      'mspInProductTrialsOverlayWindowType',
      this.overlayWindow.type
    );
    mspInProductTrialsOverlayContentWebComponent.injectDependencies(this.dependencies);
    this.overlayWindow.component = GzOverlay.show(
      {
        content: mspInProductTrialsOverlayContentWebComponent,
        position: config.position,
      },
      targetElement
    );

    await mspInProductTrialsOverlayContentWebComponent.start();

    if (this.mspInProductTrialsOverlayState === MspInProductTrialsOverlayState.OverlayDisplayedWithCheckbox) {
      // show checkbox inside overlay
      toggleVisibility(
        (this.overlayWindow.component?.content as MspInProductTrialsOverlayContentWebComponent).controller.view.dom
          .$dontShowThisAgainForm,
        true
      );
    }

    this.overlayWindow.component?.addEventListener(
      MspInProductTrialsOverlayWindowEventType.Default,
      (event: OnMspInProductTrialsOverlayWindowButtonClick) => {
        this.onMspInProductTrialsOverlayWindowButtonClick(event.detail.eventType);
      }
    );
  }

  private async createCompaniesOverlay(
    gridElement: GridViewWebComponent<any, any>,
    targetElement: HTMLElement,
  ): Promise<void> {
    this.overlayWindow = { type: MspInProductTrialsOverlayWindowType.companiesOverlay };
    const config = getMspInProductTrialsOverlayWindowConfigByWindowType(this.overlayWindow.type, this.menuState);

    if (!targetElement) {
      return;
    }

    const gzInProductTrialsOverlayWindow = document.createElement(
      WEB_COMPONENT_MSP_IN_PRODUCT_TRIALS_OVERLAY_CONTENT
    ) as MspInProductTrialsOverlayContentWebComponent;

    gzInProductTrialsOverlayWindow.setAttribute('mspInProductTrialsOverlayWindowType', this.overlayWindow.type);
    gzInProductTrialsOverlayWindow.injectDependencies(this.dependencies);

    this.overlayWindow.component = GzOverlay.show(
      {
        content: gzInProductTrialsOverlayWindow,
        position: config.position,
      },
      targetElement
    );

    await gzInProductTrialsOverlayWindow.start();

    this.updateCompaniesOverlayPosition(gridElement, targetElement);

    this.overlayWindow.component?.addEventListener(
      MspInProductTrialsOverlayWindowEventType.Default,
      (event: OnMspInProductTrialsOverlayWindowButtonClick) => {
        this.onMspInProductTrialsOverlayWindowButtonClick(event.detail.eventType, gridElement);
      }
    );

    document.addEventListener('click', event => {
      const isClickWithinBoundariesOfOverlay = event
        .composedPath()
        .includes(this.overlayWindow.component as GzInfoOverlayWebComponent);
      if (!isClickWithinBoundariesOfOverlay) {
        this.overlayWindow.component?.close(true);
      }
    });

    window.addEventListener('resize', () => {
      this.updateCompaniesOverlayPosition(gridElement, targetElement);
    });
  }

  private updateMenuOverlayPosition(shouldHideWhenOffscreen: boolean = false): void {
    const targetsByMenuItem = getTargetForMenuStateByMenuItem(this.menuState);
    const targetElement = Ext.getCmp(targetsByMenuItem[MenuItem.Company]).getEl().dom;

    if (!targetElement) {
      return;
    }

    if (shouldHideWhenOffscreen && targetElement.getBoundingClientRect().top < 0) {
      this.overlayWindow.component?.close(false);
      return;
    }

    if (!this.overlayWindow.component?.isOpen) {
      this.overlayWindow.component?.open();
    }

    GzOverlay.updatePosition(this.overlayWindow.component, targetElement);
  }

  private updateCompaniesOverlayPosition(gridElement: GridViewWebComponent<any, any>, target: HTMLElement): void {
    if (gridElement.controller?.view.dom.$grid.getBoundingClientRect().top > target.getBoundingClientRect().top) {
      this.overlayWindow.component?.close(false);
      if (this.overlayWindow.component) {
        this.overlayWindow.component.style.pointerEvents = 'none';
      }
      return;
    }

    (this.overlayWindow.component as GzInfoOverlayWebComponent).position = OverlayPosition.LEFT_TOP;

    GzOverlay.updatePosition(this.overlayWindow.component, target);

    if (!this.overlayWindow.component?.isOpen) {
      this.overlayWindow.component?.open();
      if (this.overlayWindow.component) {
        this.overlayWindow.component.style.pointerEvents = 'auto';
      }
    }

    if (
      gridElement.getBoundingClientRect().bottom <
      (this.overlayWindow.component as GzInfoOverlayWebComponent).getBoundingClientRect().bottom
    ) {
      (this.overlayWindow.component as GzInfoOverlayWebComponent).position = OverlayPosition.LEFT_BOTTOM;
    }

    GzOverlay.updatePosition(this.overlayWindow.component, target);
  }

  private async onMspInProductTrialsOverlayWindowButtonClick(
    eventType: MspInProductTrialsOverlayWindowEventType,
    mainGrid?: GridViewWebComponent<any, any>,
  ): Promise<void> {
    switch (eventType) {
      case MspInProductTrialsOverlayWindowEventType.LearnMoreMenuOverlay:
        try {
          analytics.trackPageInteraction({
            reference: this.MSP_IN_PRODUCT_TRIALS_OVERLAYS_TRACKING_NAME + ':first-overlay',
            asset: 'explore',
          });
        } finally {
          this.closeMspInProductTrialsOverlayWindow(true);
          NavigationHelper.navigateTo('/companies');
          setTimeout(this.triggerCompaniesRedirectOnLearnMoreMenuOverlay.bind(this), 300);
        }
        break;

      case MspInProductTrialsOverlayWindowEventType.HowTo:
        let urlToOpen = 'https://www.bitdefender.com/business/support/en/77211-425478-msp-product-trials.html';
        urlToOpen+= `?icid=button|b|${this.MSP_IN_PRODUCT_TRIALS_OVERLAYS_TRACKING_NAME}|overlay|how-to`;
        window.open(urlToOpen, '_blank');
        break;

      case MspInProductTrialsOverlayWindowEventType.DismissMenuOverlay:
        try {
          analytics.trackPageInteraction({
            reference: this.MSP_IN_PRODUCT_TRIALS_OVERLAYS_TRACKING_NAME + ':first-overlay',
            asset: 'dismiss',
          });
        } finally {
          const dontShowThisAgainForm = (
            this.overlayWindow.component?.content as MspInProductTrialsOverlayContentWebComponent
          ).controller.view.dom.$dontShowThisAgainForm;
          this.closeMspInProductTrialsOverlayWindow(false, dontShowThisAgainForm.value as boolean);
        }
        break;

      case MspInProductTrialsOverlayWindowEventType.DismissCompaniesOverlay:
        try {
          analytics.trackPageInteraction({
            reference: this.MSP_IN_PRODUCT_TRIALS_OVERLAYS_TRACKING_NAME + ':second-overlay',
            asset: 'dismiss',
          });
        } finally {
          this.overlayWindow.component?.close(true);
        }
        break;

      case MspInProductTrialsOverlayWindowEventType.LearnMoreCompaniesOverlay:
        try {
          analytics.trackPageInteraction({
            reference: this.MSP_IN_PRODUCT_TRIALS_OVERLAYS_TRACKING_NAME + ':second-overlay',
            asset: 'learn-more',
          });
        } finally {
          this.overlayWindow.component?.close(true);
          if (mainGrid) {
            const firstEligibleCompany = mainGrid.controller?.view.dom.$grid.items[0];
            mainGrid.dispatchEvent(new OnCompanyNameClick(firstEligibleCompany.companyId, firstEligibleCompany.companyName));
            setTimeout(() => {
              const gzDialogContainer = document.querySelector('gz-dialog');
              const editCompanyPanel = gzDialogContainer?.shadowRoot?.querySelector(WEB_COMPONENT_EDIT_COMPANY_PANEL) as EditCompanyPanelWebComponent;
              editCompanyPanel.controller.selectTab(TabIndex.MSP_IN_PRODUCT_TRIALS);
            }, 300);
          }
        }
        break;
    }
  }

  private closeMspInProductTrialsOverlayWindow(alreadyExplored = true, doNotShowThisAgain: boolean = false): void {
    const overlayNewState = alreadyExplored || doNotShowThisAgain
                            ? MspInProductTrialsOverlayState.CompletelyDismissed
                            : MspInProductTrialsOverlayState.SoftDismissed;

    try {
      if (doNotShowThisAgain) {
        analytics.trackPageInteraction({
          reference: this.MSP_IN_PRODUCT_TRIALS_OVERLAYS_TRACKING_NAME + ':first-overlay',
          asset: 'dont-show-this-again',
        });
      }
    } finally {
      this.dependencies.api.clients.MspInProductTrials.SetMspInProductTrialsOverlayState(overlayNewState).toPromise();
      this.overlayWindow.component?.close(true);
    }
  }

  private async triggerCompaniesRedirectOnLearnMoreMenuOverlay(): Promise<void> {
    const companiesApp = document.getElementsByTagName(WEB_COMPONENT_COMPANIES_APP)[0] as CompaniesAppWebComponent;
    const companiesGrid = companiesApp.controller?.view.dom.$companiesGrid as CompaniesGridWebComponent;
    const mainGrid = companiesGrid.controller?.view.dom.$grid as GridViewWebComponent<any, any>;

    // apply filtering so that only available companies for msp trial are shown
    const newFilters = FILTERS as Array<any>;
    newFilters.push({ [ColumnKeys.MSP_IN_PRODUCT_TRIAL_STATUS]: { in: [MspInProductTrialStatus.TRIAL_AVAILABLE] } });

    mainGrid.propertiesSet = false;
    mainGrid.filters = newFilters;
    mainGrid.propertiesSet = true;

    await mainGrid.controller?.handleGridItemsLoad();
    setTimeout(() => {
      if (!mainGrid.controller?.view.dom.$grid.items.length) {
        return;
      }

      // show the overlay targeted to the first company available
      const firstRow = mainGrid.controller?.view.gridTable.querySelector('tbody tr:first-child') as HTMLElement;
      const thirdCell = firstRow.querySelector('td:nth-child(3)') as HTMLElement;
      this.createCompaniesOverlay(mainGrid, thirdCell);
      mainGrid.controller.view.gridTable.addEventListener('scroll', () => {
        this.onGridScrollEvent(mainGrid, thirdCell);
      });
    }, 300);
  }
}
